import { Box, Button, Group, Paper, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import { FiCalendar, FiFilter, FiSearch } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { DrofusChangeTypeFilter } from "../components/DrofusChangeTypeFilter";
import { DrofusDisciplineBadge } from "../components/DrofusDisciplineBadge";
import PnsTreePanelContainer from "../DesignTab/routing/PnsTreePanelContainer";
import { clearSelectedPnsIdsInRedux } from "../DesignTab/routing/pnsSelectionSlice";
import {
  DROFUS_ENDRINGER_DISCIPLINE_OPTIONS,
  selectDrofusEndringerViewMode,
  resetDrofusEndringerFilters,
  selectDrofusEndringerState,
  setDrofusEndringerDateFrom,
  setDrofusEndringerDateTo,
  setDrofusEndringerSearch,
  setDrofusEndringerViewMode,
  toggleDrofusEndringerChangeType,
  toggleDrofusEndringerDiscipline,
  type IDrofusEndringerViewMode,
} from "./drofusEndringerSlice";

export const DrofusEndringerFilterBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectDrofusEndringerState);
  const viewMode = useAppSelector(selectDrofusEndringerViewMode);

  return (
    <Paper withBorder radius="sm" p="sm" w={300} style={{ flexShrink: 0, overflowY: "auto" }}>
      <Stack gap="md">
        <Group gap="xs">
          <FiFilter size={16} />
          <Text fw={700}>Filter</Text>
        </Group>

        <Stack gap={6}>
          <Text size="xs" fw={700} c="dimmed">
            VISNING
          </Text>
          <SegmentedControl
            size="xs"
            color="blue"
            fullWidth
            value={viewMode}
            onChange={(value) =>
              dispatch(setDrofusEndringerViewMode(value as IDrofusEndringerViewMode))
            }
            data={[
              { value: "changes", label: "Alle endringer" },
              { value: "rooms", label: "Gruppert per rom" },
            ]}
            styles={{
              root: {
                border: "1px solid var(--mantine-color-blue-3)",
                backgroundColor: "var(--mantine-color-blue-0)",
              },
              label: {
                fontWeight: 700,
              },
            }}
          />
        </Stack>

        <TextInput
          label="Søk"
          placeholder="Rom, artikkel, felt..."
          leftSection={<FiSearch size={14} />}
          value={state.search}
          onChange={(event) => dispatch(setDrofusEndringerSearch(event.currentTarget.value))}
        />

        <Stack gap={6}>
          <Text size="xs" fw={700} c="dimmed">
            PNS
          </Text>
          <Box h={320} style={{ minHeight: 0 }}>
            <PnsTreePanelContainer />
          </Box>
        </Stack>

        <Stack gap="xs">
          <Group gap="xs">
            <FiCalendar size={16} />
            <Text fw={600} size="sm">
              Dato
            </Text>
          </Group>
          <Group grow align="flex-start">
            <TextInput
              type="date"
              label="Fra"
              value={state.dateFrom}
              onChange={(event) => dispatch(setDrofusEndringerDateFrom(event.currentTarget.value))}
            />
            <TextInput
              type="date"
              label="Til"
              value={state.dateTo}
              onChange={(event) => dispatch(setDrofusEndringerDateTo(event.currentTarget.value))}
            />
          </Group>
        </Stack>

        <FilterChipGroup label="Type">
          <DrofusChangeTypeFilter
            selectedChangeTypes={state.changeTypes}
            onToggleChangeType={(changeType) =>
              dispatch(toggleDrofusEndringerChangeType(changeType))
            }
          />
        </FilterChipGroup>

        <FilterChipGroup label="Fag">
          {DROFUS_ENDRINGER_DISCIPLINE_OPTIONS.map((option) => (
            <DrofusDisciplineBadge
              key={option.value}
              discipline={option.label}
              active={state.affectedDisciplines.includes(option.value)}
              size="md"
              onClick={() => dispatch(toggleDrofusEndringerDiscipline(option.value))}
            />
          ))}
        </FilterChipGroup>

        <Button
          variant="light"
          onClick={() => {
            dispatch(resetDrofusEndringerFilters());
            dispatch(clearSelectedPnsIdsInRedux());
          }}
        >
          Tilbakestill filter
        </Button>
      </Stack>
    </Paper>
  );
};

const FilterChipGroup: React.FC<React.PropsWithChildren<{ label: string }>> = ({
  label,
  children,
}) => (
  <Stack gap={6}>
    <Text size="xs" fw={700} c="dimmed">
      {label.toUpperCase()}
    </Text>
    <Group gap={6}>{children}</Group>
  </Stack>
);
